/**
 * DialogService - Abstraction layer for dialogs (alert, confirm, prompt)
 *
 * Provides a unified API that works with PrimeVue.
 *
 * Usage:
 *   this.$dialog.confirm({
 *       message: 'Are you sure?',
 *       onConfirm: () => { ... }
 *   });
 */
class DialogService {
    constructor() {
        this.app = null;
    }

    init(app) {
        this.app = app;
        app.provide('dialog', this);
    }

    get $confirm() {
        return this.app?.config.globalProperties.$confirm;
    }

    alert(params) {
        window.dispatchEvent(
            new CustomEvent('primevue-dialog-request', {
                detail: {
                    type: 'alert',
                    title: params.title,
                    message: params.message,
                    confirmText: params.confirmText,
                    confirmButtonClass: this.getButtonClass(params.type),
                    onConfirm: params.onConfirm,
                },
            })
        );
    }

    confirm(params) {
        window.dispatchEvent(
            new CustomEvent('primevue-dialog-request', {
                detail: {
                    type: 'confirm',
                    title: params.title,
                    message: params.message,
                    confirmText: params.confirmText,
                    confirmBadge: params.confirmBadge,
                    cancelText: params.cancelText,
                    confirmButtonClass: this.getButtonClass(params.type),
                    onConfirm: params.onConfirm,
                    onCancel: params.onCancel,
                    // Optional secondary action (e.g. "View usage") rendered as a third button
                    secondaryText: params.secondaryText,
                    secondaryButtonClass: params.secondaryButtonClass,
                    onSecondary: params.onSecondary,
                    focusRestoreEl: params.focusRestoreEl,
                },
            })
        );
    }

    prompt(params) {
        window.dispatchEvent(
            new CustomEvent('primevue-dialog-request', {
                detail: {
                    type: 'prompt',
                    title: params.title,
                    message: params.message,
                    inputAttrs: params.inputAttrs,
                    confirmText: params.confirmText,
                    cancelText: params.cancelText,
                    confirmButtonClass: this.getButtonClass(params.type),
                    onConfirm: params.onConfirm,
                    onCancel: params.onCancel,
                },
            })
        );
    }

    getIcon(type) {
        // Icon logic handled in component if needed, or passed as param.
        // For now, simple text/button logic.
        return '';
    }

    getButtonClass(type) {
        if (type === 'is-danger') return 'p-button-danger';
        if (type === 'is-success') return 'p-button-success';
        if (type === 'is-warning') return 'p-button-warning';
        return '';
    }
}

const dialogService = new DialogService();

export default {
    install(app) {
        dialogService.init(app);
        app.config.globalProperties.$dialog = dialogService;
    },
};

export { dialogService as DialogService };
